import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAccommodationStore } from '../stores/accommodationStore';
import { RoomHeader } from '../components/accommodation/room/RoomHeader';
import { RoomStats } from '../components/accommodation/room/RoomStats';
import { RoomFilters } from '../components/accommodation/room/RoomFilters';
import { RoomTable } from '../components/accommodation/room/RoomTable';
import { RoomViewModal } from '../components/accommodation/room/RoomViewModal';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { Room } from '../types/accommodation';
import { usePackagesStore } from '../stores/packagesStore';
import { CheckInOutChart } from '../components/accommodation/CheckInOutChart';
import { usePilgrimGuideStore } from '../stores/pilgrimGuideStore';
import { LoadingProgress } from '../components/common/LoadingProgress';
import { useLanguage } from '../contexts/LanguageContext';
import { checkDateOverlap } from '../utils/dateUtils';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import toast from 'react-hot-toast';
import { AddRoomModal } from '../components/accommodation/room/AddRoomModal';

const ITEMS_PER_PAGE_KEY = 'room_view_items_per_page';
const DEFAULT_ITEMS_PER_PAGE = 50;

export const RoomViewPage = () => {
  const { translations } = useLanguage();
  const { tableId } = useParams<{ tableId: string }>();
  const { tables, loading, error, fetchTables, updateRooms } = useAccommodationStore();
  const { packages, fetchPackages } = usePackagesStore();
  const { links, loading: linksLoading, error: linksError, initialized: linksInitialized, progress, fetchLinks } = usePilgrimGuideStore();
  const [selectedRoom, setSelectedRoom] = useState<number | null>(null);
  const [showOnlyMyPilgrims, setShowOnlyMyPilgrims] = useState(false);
  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');
  const [isAddRoomModalOpen, setIsAddRoomModalOpen] = useState(false);
  const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState(false);
  const [roomToRemove, setRoomToRemove] = useState<number | null>(null);

  // Filters state
  const [searchTerm, setSearchTerm] = useState('');
  const [roomType, setRoomType] = useState('');
  const [occupancyStatus, setOccupancyStatus] = useState('');
  const [packageFilter, setPackageFilter] = useState('');
  const [checkInFilter, setCheckInFilter] = useState('');
  const [checkOutFilter, setCheckOutFilter] = useState('');
  const [availablePeriodStart, setAvailablePeriodStart] = useState('');
  const [availablePeriodEnd, setAvailablePeriodEnd] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const savedSize = localStorage.getItem(ITEMS_PER_PAGE_KEY);
    return savedSize ? parseInt(savedSize) : DEFAULT_ITEMS_PER_PAGE;
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [genderFilter, setGenderFilter] = useState<'all' | 'male' | 'female'>('all');
  const [unaccommodatedPilgrims, setUnaccommodatedPilgrims] = useState<any[]>([]);

  useEffect(() => {
    if (!tables[tableId!]) {
      fetchTables();
    }
    fetchPackages();
  }, [tableId, tables, fetchTables, fetchPackages]);

  // Initialize links if not already done
  useEffect(() => {
    if (!linksInitialized) {
      const guideId = userRole === 'guide' ? userId : undefined;
      fetchLinks(guideId);
    }
  }, [linksInitialized, fetchLinks, userRole, userId]);

  useEffect(() => {
    localStorage.setItem(ITEMS_PER_PAGE_KEY, itemsPerPage.toString());
  }, [itemsPerPage]);

  // Get unaccommodated pilgrims from links store
  useEffect(() => {
    const getUnaccommodatedPilgrims = async () => {
      try {
        const table = tables[tableId!];
        if (!table) return;

        // Get all pilgrims linked to this guide/organizer
        const approvedLinks = Object.values(links).filter(link => {
          if (link.status !== 'approved') return false;
          if (userRole === 'guide') return link.guideId === userId;
          if (userRole === 'organizer') return link.organizerId === userId;
          return true;
        });

        // Get all pilgrim IDs that are already accommodated in this hotel
        const accommodatedPilgrims = new Set<string>();
        table.r.forEach(room => {
          room.b.forEach(bed => {
            if (bed.a) {
              bed.a.forEach(assignment => {
                accommodatedPilgrims.add(assignment.pid);
              });
            }
          });
        });

        // Filter out pilgrims who are already accommodated
        const unaccommodatedLinks = approvedLinks.filter(link => 
          !accommodatedPilgrims.has(link.pilgrimId)
        );

        // Get package details for each pilgrim to check room type
        const unaccommodatedWithDetails = unaccommodatedLinks.map(link => {
          const pkg = packages[link.packageName];
          let roomType = '';

          if (pkg) {
            if (table.hn === pkg.mainMakkahHotel) {
              roomType = link.mainMakkahRoomType;
            } else if (pkg.shiftingMakkahHotel && table.hn === pkg.shiftingMakkahHotel) {
              roomType = link.shiftingMakkahRoomType;
            } else if (table.hn === pkg.madinahHotel) {
              roomType = link.madinahRoomType;
            }
          }

          return {
            id: link.pilgrimId,
            name: link.pilgrimName,
            passportNumber: link.passportNumber,
            gender: link.gender,
            country: link.residenceCountry,
            roomType,
            packageName: link.packageName,
            guideId: link.guideId,
            guideName: link.guideName,
            organizerId: link.organizerId,
            organizerName: link.organizerName
          };
        });

        setUnaccommodatedPilgrims(unaccommodatedWithDetails);
      } catch (error) {
        console.error('Error getting unaccommodated pilgrims:', error);
      }
    };

    if (linksInitialized) {
      getUnaccommodatedPilgrims();
    }
  }, [tableId, tables, links, packages, userRole, userId, linksInitialized]);

  if (loading || (linksLoading && !linksInitialized)) {
    return <LoadingSpinner />;
  }

  if (error || linksError) {
    return <ErrorMessage message={error || linksError} />;
  }

  const table = tables[tableId!];
  if (!table) return <ErrorMessage message="جدول التسكين غير موجود" />;

  // Filter rooms
  const filteredRooms = table.r.filter((room: Room) => {
    // Search filter
    const matchesSearch = !searchTerm || 
      room.n.toString().includes(searchTerm) ||
      room.b.some(bed => 
        bed.a?.some(assignment => 
          assignment.pn.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );

    // Room type filter
    const matchesType = !roomType || room.t === roomType;

    // Occupancy status filter
    const matchesOccupancy = !occupancyStatus || (
      occupancyStatus === 'occupied' ? (room.o === room.c) :
      occupancyStatus === 'available' ? (room.o === 0) :
      occupancyStatus === 'partial' ? (room.o > 0 && room.o < room.c) :
      true
    );

    // Package filter
    const matchesPackage = !packageFilter || room.b.some(bed => 
      bed.a?.some(assignment => {
        const link = Object.values(links).find(l => l.pilgrimId === assignment.pid);
        return link?.packageName === packageFilter;
      })
    );

    // My pilgrims filter
    const matchesMyPilgrims = !showOnlyMyPilgrims || room.b.some(bed =>
      bed.a?.some(assignment => {
        const link = Object.values(links).find(l => l.pilgrimId === assignment.pid);
        if (userRole === 'guide') {
          return link?.guideId === userId;
        } else if (userRole === 'organizer') {
          return link?.organizerId === userId;
        }
        return true;
      })
    );

    // Date range filter
    const matchesDateRange = (!checkInFilter && !checkOutFilter) || room.b.some(bed =>
      bed.a?.some(assignment => {
        const assignmentStart = assignment.ci;
        const assignmentEnd = assignment.co;
        const filterStart = checkInFilter ? new Date(checkInFilter).getTime() : null;
        const filterEnd = checkOutFilter ? new Date(checkOutFilter).getTime() : null;

        if (filterStart && filterEnd) {
          return assignmentStart <= filterEnd && assignmentEnd >= filterStart;
        } else if (filterStart) {
          return assignmentEnd >= filterStart;
        } else if (filterEnd) {
          return assignmentStart <= filterEnd;
        }
        return true;
      })
    );

    // Available period filter (FIXED)
    const matchesAvailablePeriod = (!availablePeriodStart || !availablePeriodEnd) || 
      room.b.some(bed => {
        // If the bed has no assignments, it's always available
        if (!bed.a || bed.a.length === 0) return true;
        
        // Convert yyyy-mm-dd to timestamp
        const periodStartTime = new Date(availablePeriodStart).getTime();
        const periodEndTime = new Date(availablePeriodEnd).getTime();
        
        // Check if this specific bed has no conflict
        const { hasOverlap } = checkDateOverlap(periodStartTime, periodEndTime, bed.a);
        
        // If there's no overlap, this bed is available during the period
        return !hasOverlap;
      });

    // Gender filter
    const matchesGender = genderFilter === 'all' || room.b.some(bed =>
      bed.a?.some(assignment => 
        genderFilter === 'male' ? assignment.g === 'M' : assignment.g === 'F'
      )
    );

    return matchesSearch && matchesType && matchesOccupancy && 
           matchesPackage && matchesDateRange && matchesMyPilgrims &&
           matchesAvailablePeriod && matchesGender;
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredRooms.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedRooms = filteredRooms.slice(startIndex, startIndex + itemsPerPage);

  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1); // Reset to first page when changing items per page
    localStorage.setItem(ITEMS_PER_PAGE_KEY, value.toString());
  };

  // Handle adding new room
  const handleAddRoom = () => {
    setIsAddRoomModalOpen(true);
  };

  // Submit new room to the database
  const handleAddRoomSubmit = async (newRoomData: { 
    roomNumber: number; 
    roomType: 'd' | 't' | 'q';
  }) => {
    try {
      // Check if the room number already exists
      const roomExists = table.r.some(room => room.n === newRoomData.roomNumber);
      if (roomExists) {
        toast.error(translations?.roomView?.roomExists || 'Room number already exists');
        return;
      }

      // Map room type to capacity
      const capacityMap = {
        'd': 2,
        't': 3,
        'q': 4
      };
      
      // Create new room object
      const newRoom: Room = {
        n: newRoomData.roomNumber,
        t: newRoomData.roomType,
        c: capacityMap[newRoomData.roomType],
        o: 0,
        b: Array(capacityMap[newRoomData.roomType]).fill(null).map(() => ({ a: [] }))
      };

      // Add room to the table
      const updatedRooms = [...table.r, newRoom];
      
      // Update the table in the database
      await updateRooms(tableId!, updatedRooms);
      
      toast.success(translations?.roomView?.roomAdded || 'Room added successfully');
      setIsAddRoomModalOpen(false);
    } catch (error: any) {
      console.error('Error adding room:', error);
      toast.error(error.message || translations?.roomView?.addRoomError || 'Failed to add room');
    }
  };

  // Handle removing a room
  const handleRemoveRoom = (roomNumber: number) => {
    setRoomToRemove(roomNumber);
    setIsRemoveConfirmOpen(true);
  };

  // Confirm and remove the room
  const confirmRemoveRoom = async () => {
    if (roomToRemove === null) return;
    
    try {
      // Find the room to remove
      const room = table.r.find(r => r.n === roomToRemove);
      if (!room) {
        toast.error(translations?.roomView?.roomNotFound || 'Room not found');
        return;
      }

      // Check if room has occupants
      if (room.o > 0) {
        toast.error(translations?.roomView?.cannotRemoveOccupied || 'Cannot remove room with occupants');
        return;
      }

      // Remove the room
      const updatedRooms = table.r.filter(r => r.n !== roomToRemove);
      
      // Update the table in the database
      await updateRooms(tableId!, updatedRooms);
      
      toast.success(translations?.roomView?.roomRemoved || 'Room removed successfully');
    } catch (error: any) {
      console.error('Error removing room:', error);
      toast.error(error.message || translations?.roomView?.removeRoomError || 'Failed to remove room');
    } finally {
      setRoomToRemove(null);
      setIsRemoveConfirmOpen(false);
    }
  };

  return (
    <div className="space-y-6">
      <RoomHeader 
        tableName={table.tn}
        hotelName={table.hn}
      />

      {/* Loading Progress */}
      {(linksLoading && !linksInitialized) && progress.total > 0 && (
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <LoadingProgress
            progress={progress.current}
            total={progress.total}
            label={translations?.loading?.loadingLinks || 'Loading links...'}
          />
        </div>
      )}

      {/* Check-in/Check-out Chart */}
      <CheckInOutChart hotelName={table.hn} />

      <RoomStats 
        rooms={table.r}
      />

      <RoomFilters
        table={table}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        roomType={roomType}
        onRoomTypeChange={setRoomType}
        occupancyStatus={occupancyStatus}
        onOccupancyStatusChange={setOccupancyStatus}
        packageFilter={packageFilter}
        onPackageFilterChange={setPackageFilter}
        checkInFilter={checkInFilter}
        onCheckInFilterChange={setCheckInFilter}
        checkOutFilter={checkOutFilter}
        onCheckOutFilterChange={setCheckOutFilter}
        availablePeriodStart={availablePeriodStart}
        onAvailablePeriodStartChange={setAvailablePeriodStart}
        availablePeriodEnd={availablePeriodEnd}
        onAvailablePeriodEndChange={setAvailablePeriodEnd}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={handleItemsPerPageChange}
        rooms={table.r}
        showOnlyMyPilgrims={showOnlyMyPilgrims}
        onShowOnlyMyPilgrimsChange={setShowOnlyMyPilgrims}
        genderFilter={genderFilter}
        onGenderFilterChange={setGenderFilter}
        unaccommodatedPilgrims={unaccommodatedPilgrims}
        onPilgrimSelect={(pilgrim) => {
          setSelectedRoom(null); // Reset selected room
          // Handle pilgrim selection
        }}
      />

      <div className="bg-white rounded-lg shadow">
        <RoomTable 
          rooms={paginatedRooms}
          onViewRoom={setSelectedRoom}
          onAddRoom={userRole === 'admin' ? handleAddRoom : undefined}
          onRemoveRoom={userRole === 'admin' ? handleRemoveRoom : undefined}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          startIndex={startIndex}
          totalRooms={filteredRooms.length}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </div>

      {selectedRoom !== null && (
        <RoomViewModal
          isOpen={true}
          onClose={() => setSelectedRoom(null)}
          tableId={tableId!}
          roomNumber={selectedRoom}
        />
      )}

      {/* Add Room Modal */}
      {isAddRoomModalOpen && (
        <AddRoomModal
          isOpen={true}
          onClose={() => setIsAddRoomModalOpen(false)}
          onSubmit={handleAddRoomSubmit}
        />
      )}

      {/* Confirm Remove Room Dialog */}
      <ConfirmDialog
        isOpen={isRemoveConfirmOpen}
        onClose={() => setIsRemoveConfirmOpen(false)}
        onConfirm={confirmRemoveRoom}
        title={translations?.roomView?.removeRoomTitle || 'Remove Room'}
        message={
          translations?.roomView?.removeRoomConfirm?.replace(
            '{roomNumber}',
            String(roomToRemove)
          ) ||
          `Are you sure you want to remove room ${roomToRemove}? This action cannot be undone.`
        }
        type="danger"
        confirmText={translations?.common?.delete || 'Delete'}
        cancelText={translations?.common?.cancel || 'Cancel'}
      />
    </div>
  );
};